"use client";

import type { CSSProperties, ReactElement } from "react";
import { Cmarkdownvisual, CmarkdownvisualType } from "../Cmarkdownvisual";
import { GanttChartBody } from "./GanttChartBody";
import { GanttCompactPreview } from "./GanttCompactPreview";
import { GanttToolbarControls } from "./GanttToolbarControls";
import type { CmarkdownganttController } from "./useCmarkdownganttController";

const GANTT_METRIC_UNIT = "TASKS";

export interface CmarkdownganttViewProps {
  controller: CmarkdownganttController;
  className?: string;
  style?: CSSProperties;
}

export function CmarkdownganttView({
  controller,
  className,
  style,
}: CmarkdownganttViewProps): ReactElement {
  const controls = (
    <GanttToolbarControls
      onScrollToToday={controller.zoom.handleScrollToToday}
      onZoomOut={controller.zoom.handleZoomOut}
      onZoomIn={controller.zoom.handleZoomIn}
      isTodayDisabled={controller.layout.todayX == null}
      isZoomOutDisabled={controller.zoom.isAtMinZoom}
      isZoomInDisabled={controller.zoom.isAtMaxZoom}
    />
  );
  const modalControls = (
    <GanttToolbarControls
      onScrollToToday={controller.zoom.handleScrollToToday}
      onZoomOut={controller.zoom.handleZoomOut}
      onZoomIn={controller.zoom.handleZoomIn}
      isTodayDisabled={controller.layout.todayX == null}
      isZoomOutDisabled={controller.zoom.isAtMinZoom}
      isZoomInDisabled={controller.zoom.isAtMaxZoom}
    />
  );
  const compactPreview = controller.compactPreviewModel ? (
    <GanttCompactPreview model={controller.compactPreviewModel} />
  ) : null;

  return (
    <Cmarkdownvisual
      dataElScope="Cmarkdowngantt/CmarkdownganttView"
      type={CmarkdownvisualType.Gantt}
      title={controller.parsed.title}
      controls={controls}
      modalControls={modalControls}
      contentStyle={{
        height: controller.containerHeight,
        minHeight: controller.containerHeight,
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
      className={className}
      style={style}
      compactPreview={compactPreview}
      metric={{
        value: controller.parsed.tasks.length,
        unit: GANTT_METRIC_UNIT,
      }}
    >
      <GanttChartBody
        labelsPaneRef={controller.timelineViewport.labelsPaneRef}
        timelineScrollRef={controller.timelineViewport.timelineScrollRef}
        ganttIdPrefix={controller.ganttIdPrefix}
        layout={controller.layout}
        hoveredTaskId={controller.hover.hoveredTaskId}
        highlightedTaskIds={controller.hover.highlightedTaskIds}
        dependencyLineLayouts={controller.hover.dependencyLineLayouts}
        tooltipPlacementData={controller.hover.tooltipPlacementData}
        scrollHintState={controller.timelineViewport.scrollHintState}
        isEntranceComplete={controller.isEntranceComplete}
        onToggleSection={controller.onToggleSection}
        onHoveredTaskIdChange={controller.hover.setHoveredTaskId}
      />
    </Cmarkdownvisual>
  );
}
